import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  CalendarDays,
  Camera,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Download,
  List,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import { getClassifications, getClassification, deleteClassification, photoUrl, resolvePhoto } from "../../lib/metalApi";
import { downloadCsv, downloadPdf, downloadXlsx, fetchPhotosFor } from "../../lib/exporters";
import { AuthImage } from "./AuthImage";

const EASE = [0.16, 1, 0.3, 1];

const dayKey = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

// Cache miniature condivisa (id classificazione → data URI foto, "" = nessuna foto)
const thumbCache = new Map();

// Coda a concorrenza limitata: il server non offre miniature leggere e ogni
// dettaglio pesa ~1.5MB; senza coda le card visibili saturano la banda.
const thumbQueue = (() => {
  const pending = [];
  let running = 0;
  const MAX = 3;
  const pump = () => {
    while (running < MAX && pending.length) {
      const job = pending.shift();
      running++;
      Promise.resolve()
        .then(job)
        .catch(() => {})
        .finally(() => {
          running--;
          pump();
        });
    }
  };
  return { add: (job) => { pending.push(job); pump(); } };
})();

const ThumbImage = ({ id, className, onZoom }) => {
  const [src, setSrc] = useState(() => (id && thumbCache.has(id) ? thumbCache.get(id) : null));
  const ref = useRef(null);

  useEffect(() => {
    if (src !== null || !id) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        thumbQueue.add(() =>
          getClassification(id).then(async (full) => {
            const raw = (full?.photos || [])[0];
            const p = raw ? await resolvePhoto(raw) : "";
            thumbCache.set(id, p);
            setSrc(p);
          })
        );
      },
      { rootMargin: "300px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [id, src]);

  if (src)
    return (
      <img
        ref={ref}
        src={src}
        alt=""
        data-testid="card-thumb"
        onClick={onZoom ? (e) => { e.stopPropagation(); onZoom(src); } : undefined}
        className={`${className} ${onZoom ? "cursor-zoom-in" : ""}`}
      />
    );
  return (
    <div ref={ref} className={`flex items-center justify-center bg-[#17122E] ${className}`}>
      <Camera className="h-5 w-5 text-[#8B87A8]" />
    </div>
  );
};

// Cache dei riferimenti foto risolti (chiave storage/data URI → src usabile)
const resolvedCache = new Map();
const resolveCached = async (p) => {
  if (!p || typeof p !== "string") return "";
  if (p.startsWith("data:") || p.startsWith("blob:")) return p;
  if (resolvedCache.has(p)) return resolvedCache.get(p);
  const u = await resolvePhoto(p);
  resolvedCache.set(p, u);
  return u;
};

const Lightbox = ({ photos, start = 0, onClose }) => {
  const [idx, setIdx] = useState(start);
  const [srcs, setSrcs] = useState({});
  const [zoomed, setZoomed] = useState(false);
  const touchX = useRef(null);
  const total = photos.length;
  const cur = srcs[idx];

  useEffect(() => {
    let alive = true;
    (async () => {
      const targets = [idx, idx - 1, idx + 1].filter((i) => i >= 0 && i < total);
      for (const i of targets) {
        const u = await resolveCached(photos[i]);
        if (!alive) return;
        setSrcs((s) => (s[i] ? s : { ...s, [i]: u }));
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, photos]);

  useEffect(() => setZoomed(false), [idx]);

  const go = (dir) => setIdx((i) => (i + dir + total) % total);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (total > 1 && e.key === "ArrowRight") go(1);
      if (total > 1 && e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, total]);

  const NAV_BTN =
    "absolute top-1/2 z-10 -translate-y-1/2 rounded-xl border border-[#342C55] bg-[#17122E]/80 p-3 text-[#8B87A8] transition-colors duration-300 hover:border-[#34D399] hover:text-[#34D399]";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="photo-lightbox"
      onClick={onClose}
      onTouchStart={(e) => {
        touchX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchX.current === null || total < 2) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        touchX.current = null;
        if (Math.abs(dx) > 50 && !zoomed) go(dx < 0 ? 1 : -1);
      }}
      className="fixed inset-0 z-[70] touch-none bg-black/95 backdrop-blur-sm"
    >
      <button
        data-testid="lightbox-close"
        onClick={onClose}
        aria-label="Chiudi"
        className="absolute right-5 top-5 z-10 rounded-xl border border-[#342C55] bg-[#17122E]/80 p-2.5 text-[#8B87A8] transition-colors duration-300 hover:border-[#34D399] hover:text-[#34D399]"
      >
        <X className="h-5 w-5" />
      </button>

      {total > 1 && (
        <>
          <button
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            aria-label="Foto precedente"
            className={`${NAV_BTN} left-3 sm:left-6`}
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); go(1); }}
            aria-label="Foto successiva"
            className={`${NAV_BTN} right-3 sm:right-6`}
          >
            <ChevronRight className="h-6 w-6" />
          </button>
          <p
            data-testid="lightbox-counter"
            className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 rounded-full border border-[#342C55] bg-[#17122E]/80 px-4 py-1.5 font-manrope text-xs font-bold text-[#F5F5F5]"
          >
            {idx + 1} / {total}
          </p>
        </>
      )}

      <div
        className={zoomed ? "h-full w-full overflow-auto" : "flex h-full w-full items-center justify-center p-4"}
        onClick={(e) => e.stopPropagation()}
      >
        {cur ? (
          <img
            src={cur}
            alt=""
            data-testid="lightbox-image"
            onClick={() => setZoomed((z) => !z)}
            className={
              zoomed
                ? "mx-auto my-auto w-[180%] max-w-none cursor-zoom-out"
                : "max-h-[92vh] max-w-[94vw] cursor-zoom-in rounded-xl object-contain"
            }
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-[#34D399] border-t-transparent" />
          </div>
        )}
      </div>
    </motion.div>
  );
};

const fmtDate = (d, lang) =>
  new Date(d).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const Calendar = ({ items, selectedDay, onSelect }) => {
  const { lang, t } = useLanguage();
  const [view, setView] = useState(() => new Date());

  const counts = useMemo(() => {
    const m = {};
    items.forEach((c) => {
      const k = dayKey(c.date || c.created_at);
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [items]);

  const year = view.getFullYear();
  const month = view.getMonth();
  const offset = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const monthLabel = new Intl.DateTimeFormat(lang === "it" ? "it-IT" : "en-GB", {
    month: "long",
    year: "numeric",
  }).format(view);
  const todayK = dayKey(new Date());

  return (
    <div className="rounded-2xl border border-[#342C55] bg-[#221D3F] p-4 md:p-5">
      <div className="mb-4 flex items-center justify-between">
        <button
          data-testid="cal-prev"
          onClick={() => setView(new Date(year, month - 1, 1))}
          className="rounded-lg p-2 text-[#8B87A8] transition-colors duration-300 hover:bg-[#17122E] hover:text-[#34D399]"
          aria-label="prev"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <p className="font-bebas text-xl tracking-[0.12em] text-[#F5F5F5] capitalize">{monthLabel}</p>
        <button
          data-testid="cal-next"
          onClick={() => setView(new Date(year, month + 1, 1))}
          className="rounded-lg p-2 text-[#8B87A8] transition-colors duration-300 hover:bg-[#17122E] hover:text-[#34D399]"
          aria-label="next"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {t.reserved.weekdays.map((w, i) => (
          <span key={i} className="pb-2 text-center font-manrope text-[10px] font-bold uppercase text-[#8B87A8]">
            {w}
          </span>
        ))}
        {cells.map((d, i) => {
          if (d === null) return <span key={`e${i}`} />;
          const k = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const n = counts[k] || 0;
          const isSel = selectedDay === k;
          const isToday = todayK === k;
          return (
            <button
              key={k}
              data-testid={`cal-day-${d}`}
              onClick={() => onSelect(isSel ? null : k)}
              className={`relative flex aspect-square flex-col items-center justify-center rounded-xl font-manrope text-sm transition-colors duration-200 ${
                isSel
                  ? "bg-[#34D399] font-bold text-[#17122E]"
                  : n > 0
                  ? "bg-[#34D399]/10 font-semibold text-[#F5F5F5] hover:bg-[#34D399]/20"
                  : "text-[#8B87A8] hover:bg-[#17122E]"
              } ${isToday && !isSel ? "ring-1 ring-[#34D399]/60" : ""}`}
            >
              {d}
              {n > 0 && (
                <span
                  className={`mt-0.5 font-manrope text-[10px] font-bold leading-none ${
                    isSel ? "text-[#17122E]" : "text-[#34D399]"
                  }`}
                >
                  {n}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {selectedDay && (
        <button
          data-testid="cal-clear"
          onClick={() => onSelect(null)}
          className="mt-4 w-full rounded-xl border border-[#342C55] py-2.5 font-manrope text-xs font-semibold text-[#8B87A8] transition-colors duration-300 hover:border-[#34D399] hover:text-[#34D399]"
        >
          {t.reserved.allDates}
        </button>
      )}
    </div>
  );
};

const DayModal = ({ day, items, onClose, onOpen }) => {
  const { lang, t } = useLanguage();
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/80 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={onClose}
      data-testid="storico-day-modal"
    >
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-[#342C55] bg-[#221D3F] p-6 sm:rounded-3xl"
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <p className="font-manrope text-lg font-bold capitalize text-[#F5F5F5]">
              {new Date(`${day}T12:00:00`).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </p>
            <p className="mt-1 font-manrope text-xs uppercase tracking-wide text-[#8B87A8]">
              {items.length} {t.reserved.classifications.toLowerCase()}
            </p>
          </div>
          <button
            data-testid="day-modal-close"
            onClick={onClose}
            aria-label="Chiudi"
            className="rounded-lg border border-[#342C55] p-2 text-[#8B87A8] transition-colors duration-300 hover:border-[#34D399] hover:text-[#34D399]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {items.length === 0 ? (
          <p className="py-8 text-center font-manrope text-sm text-[#8B87A8]">{t.reserved.dayEmpty}</p>
        ) : (
          <div className="space-y-3">
            {items.map((c, i) => (
              <button
                key={c.id ?? i}
                data-testid={`day-item-${i}`}
                onClick={() => onOpen(c)}
                className="flex w-full items-center justify-between gap-3 rounded-xl border border-[#342C55] bg-[#17122E] px-4 py-3 text-left transition-colors duration-300 hover:border-[#34D399]/60"
              >
                <div className="min-w-0">
                  <p className="truncate font-manrope text-sm font-bold text-[#F5F5F5]">
                    {c.supplier_name || "—"}
                  </p>
                  <p className="mt-0.5 truncate font-manrope text-xs text-[#8B87A8]">
                    {c.description && c.description !== "Nessuna descrizione" ? c.description : "—"}
                    {c.created_by ? ` · ${t.reserved.by} ${c.created_by}` : ""}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {c.discount_percentage > 0 && (
                    <span className="rounded-full bg-[#34D399]/15 px-2 py-0.5 font-manrope text-xs font-bold text-[#34D399]">
                      -{c.discount_percentage}%
                    </span>
                  )}
                  <span
                    className={`rounded-full px-2 py-0.5 font-manrope text-[10px] font-bold uppercase tracking-wide ${
                      c.send_status === "sent" ? "bg-[#34D399]/15 text-[#34D399]" : "bg-amber-400/15 text-amber-300"
                    }`}
                  >
                    {c.send_status === "sent" ? t.reserved.sent : t.reserved.draft}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

const DateField = ({ value, onChange, testId, label }) => {
  const { lang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const sel = value ? new Date(`${value}T12:00:00`) : null;
  const [view, setView] = useState(() => sel || new Date());

  useEffect(() => {
    if (open) setView(sel || new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const year = view.getFullYear();
  const month = view.getMonth();
  const offset = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const monthLabel = new Intl.DateTimeFormat(lang === "it" ? "it-IT" : "en-GB", {
    month: "long",
    year: "numeric",
  }).format(view);

  return (
    <div className="relative flex-1">
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        className="flex h-11 w-full items-center justify-between gap-2 rounded-xl border border-[#342C55] bg-[#221D3F] px-3 transition-colors duration-300 hover:border-[#34D399]/60"
      >
        <span className="font-manrope text-[10px] font-bold uppercase tracking-wide text-[#8B87A8]">{label}</span>
        <span className={`font-manrope text-sm ${value ? "text-[#F5F5F5]" : "text-[#8B87A8]"}`}>
          {value ? fmtDate(sel, lang) : t.reserved.pickDate}
        </span>
        <CalendarDays className="h-4 w-4 shrink-0 text-[#8B87A8]" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            data-testid={`${testId}-popover`}
            className="absolute left-0 top-full z-50 mt-2 w-80 max-w-[calc(100vw-2.5rem)] rounded-2xl border border-[#342C55] bg-[#1D1838] p-4 shadow-2xl shadow-black/60"
          >
            <div className="mb-3 flex items-center justify-between">
              <button
                type="button"
                data-testid={`${testId}-prev`}
                onClick={() => setView(new Date(year, month - 1, 1))}
                className="rounded-lg p-1.5 text-[#8B87A8] transition-colors duration-200 hover:bg-[#17122E] hover:text-[#34D399]"
                aria-label="prev"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <p className="font-bebas text-lg capitalize tracking-[0.12em] text-[#F5F5F5]">{monthLabel}</p>
              <button
                type="button"
                data-testid={`${testId}-next`}
                onClick={() => setView(new Date(year, month + 1, 1))}
                className="rounded-lg p-1.5 text-[#8B87A8] transition-colors duration-200 hover:bg-[#17122E] hover:text-[#34D399]"
                aria-label="next"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1">
              {t.reserved.weekdays.map((w, i) => (
                <span key={i} className="pb-1 text-center font-manrope text-[10px] font-bold uppercase text-[#8B87A8]">
                  {w}
                </span>
              ))}
              {cells.map((d, i) => {
                if (d === null) return <span key={`e${i}`} />;
                const k = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                const isSel = value === k;
                return (
                  <button
                    key={k}
                    type="button"
                    data-testid={`${testId}-day-${d}`}
                    onClick={() => { onChange(k); setOpen(false); }}
                    className={`flex h-9 items-center justify-center rounded-lg font-manrope text-sm transition-colors duration-200 ${
                      isSel
                        ? "bg-[#34D399] font-bold text-[#17122E]"
                        : "text-[#F5F5F5] hover:bg-[#34D399]/15"
                    }`}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              data-testid={`${testId}-clear`}
              onClick={() => { onChange(""); setOpen(false); }}
              className="mt-3 w-full rounded-xl border border-[#342C55] py-2 font-manrope text-xs font-semibold text-[#8B87A8] transition-colors duration-300 hover:border-[#34D399] hover:text-[#34D399]"
            >
              {t.reserved.noDate}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const inRange = (c, from, to) => {
  const dk = dayKey(c.date || c.created_at);
  if (from && dk < from) return false;
  if (to && dk > to) return false;
  return true;
};

const FORMATS = ["csv", "xlsx", "pdf"];

const ExportModal = ({ items, onClose }) => {
  const { lang, t } = useLanguage();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [supplier, setSupplier] = useState("");
  const [status, setStatus] = useState("");
  const [op, setOp] = useState("");
  const [format, setFormat] = useState("xlsx");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const suppliers = useMemo(
    () => [...new Set(items.map((c) => c.supplier_name).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [items]
  );
  const operators = useMemo(
    () => [...new Set(items.map((c) => c.created_by).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [items]
  );

  const match = items.filter(
    (c) =>
      inRange(c, from, to) &&
      (!supplier || c.supplier_name === supplier) &&
      (!status || c.send_status === status) &&
      (!op || c.created_by === op)
  );

  const summaryParts = [
    from || to ? `${from || "…"} → ${to || "…"}` : null,
    supplier || null,
    status ? (status === "sent" ? t.reserved.sent : t.reserved.draft) : null,
    op || null,
  ].filter(Boolean);
  const summary = summaryParts.length ? summaryParts.join(" · ") : t.reserved.exportAll;

  const doExport = async () => {
    if (format === "csv") {
      downloadCsv(match, lang, t);
      onClose();
      return;
    }
    if (format === "pdf") {
      downloadPdf(match, lang, t, summary);
      onClose();
      return;
    }
    setBusy(true);
    setProgress({ done: 0, total: match.length });
    try {
      const photos = await fetchPhotosFor(match, thumbCache, (done, total) => setProgress({ done, total }));
      await downloadXlsx(match, photos, lang, t);
      onClose();
    } catch {
      setBusy(false);
      setProgress(null);
    }
  };

  const FIELD =
    "h-11 w-full rounded-xl border border-[#342C55] bg-[#17122E] px-3 font-manrope text-sm text-[#F5F5F5] focus:border-[#34D399] focus:outline-none";
  const LBL = "mb-1.5 block font-manrope text-[10px] font-bold uppercase tracking-[0.14em] text-[#8B87A8]";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/80 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={() => !busy && onClose()}
      data-testid="export-modal"
    >
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[88vh] w-full max-w-md overflow-y-auto rounded-t-3xl border border-[#342C55] bg-[#221D3F] p-6 sm:rounded-3xl"
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <p className="font-manrope text-lg font-bold text-[#F5F5F5]">{t.reserved.exportTitle}</p>
          <button
            data-testid="export-cancel"
            onClick={onClose}
            aria-label="Chiudi"
            className="rounded-lg border border-[#342C55] p-2 text-[#8B87A8] transition-colors duration-300 hover:border-[#34D399] hover:text-[#34D399]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="flex gap-3">
            <DateField value={from} onChange={setFrom} testId="export-from" label={t.reserved.fromDate} />
            <DateField value={to} onChange={setTo} testId="export-to" label={t.reserved.toDate} />
          </div>
          <div>
            <span className={LBL}>{t.reserved.filterSupplier}</span>
            <select data-testid="export-supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} className={FIELD}>
              <option value="">{t.reserved.exportAll}</option>
              {suppliers.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className={LBL}>{t.reserved.filterStatus}</span>
              <select data-testid="export-status" value={status} onChange={(e) => setStatus(e.target.value)} className={FIELD}>
                <option value="">{t.reserved.exportAll}</option>
                <option value="sent">{t.reserved.sent}</option>
                <option value="saved">{t.reserved.draft}</option>
              </select>
            </div>
            <div>
              <span className={LBL}>{t.reserved.filterOperator}</span>
              <select data-testid="export-operator" value={op} onChange={(e) => setOp(e.target.value)} className={FIELD}>
                <option value="">{t.reserved.exportAll}</option>
                {operators.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <span className={LBL}>{t.reserved.formatLabel}</span>
            <div className="grid grid-cols-3 gap-2">
              {FORMATS.map((f) => (
                <button
                  key={f}
                  data-testid={`export-format-${f}`}
                  onClick={() => setFormat(f)}
                  className={`rounded-xl border px-2 py-2.5 font-manrope text-[11px] font-bold transition-colors duration-300 ${
                    format === f
                      ? "border-[#34D399] bg-[#34D399]/15 text-[#34D399]"
                      : "border-[#342C55] text-[#8B87A8] hover:border-[#34D399]/50"
                  }`}
                >
                  {t.reserved.formats[f]}
                </button>
              ))}
            </div>
          </div>
        </div>

        <p data-testid="export-count" className="mt-5 text-center font-manrope text-xs text-[#8B87A8]">
          {match.length} / {items.length} {t.reserved.classifications.toLowerCase()}
        </p>
        {busy && progress && (
          <div className="mt-3">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#17122E]">
              <div
                data-testid="export-progress"
                className="h-full rounded-full bg-[#34D399] transition-[width] duration-300"
                style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%` }}
              />
            </div>
            <p className="mt-2 text-center font-manrope text-[11px] text-[#8B87A8]">
              {t.reserved.loadingPhotos} {progress.done}/{progress.total}
            </p>
          </div>
        )}
        <button
          data-testid="export-download"
          onClick={doExport}
          disabled={match.length === 0 || busy}
          className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-[#34D399] font-manrope text-sm font-bold text-[#17122E] transition-colors duration-300 hover:bg-[#2BBD8C] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          {busy ? t.reserved.loadingPhotos : t.reserved.downloadFile}
        </button>
      </motion.div>
    </motion.div>
  );
};

const DetailModal = ({ c, onClose, onZoom }) => {
  const { lang, t } = useLanguage();
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const photos = c.photos || [];
  const loading = !!c._loading;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/80 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={onClose}
      data-testid="storico-detail-modal"
    >
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-[#342C55] bg-[#221D3F] p-6 sm:rounded-3xl"
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <p className="font-manrope text-lg font-bold text-[#F5F5F5]">{c.supplier_name || "—"}</p>
            <p className="mt-1 font-manrope text-xs uppercase tracking-wide text-[#8B87A8]">
              {fmtDate(c.date || c.created_at, lang)}
              {c.created_by ? ` · ${t.reserved.by} ${c.created_by}` : ""}
            </p>
          </div>
          <button
            data-testid="detail-close"
            onClick={onClose}
            aria-label="Chiudi"
            className="rounded-lg border border-[#342C55] p-2 text-[#8B87A8] transition-colors duration-300 hover:border-[#34D399] hover:text-[#34D399]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {loading ? (
          <div
            data-testid="detail-photos-loading"
            className="mb-5 flex h-48 items-center justify-center rounded-xl border border-[#342C55] bg-[#17122E]"
          >
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#34D399] border-t-transparent" />
          </div>
        ) : photos.length > 0 ? (
          <div className="mb-5 flex gap-3 overflow-x-auto pb-1">
            {photos.map((p, i) =>
              typeof p === "string" && p.startsWith("data:") ? (
                <img
                  key={i}
                  data-testid={`detail-photo-${i}`}
                  src={p}
                  alt=""
                  onClick={() => onZoom?.(photos, i)}
                  className="h-48 w-36 shrink-0 cursor-zoom-in rounded-xl border border-[#342C55] object-cover"
                />
              ) : (
                <AuthImage
                  key={i}
                  src={photoUrl(p)}
                  onZoom={() => onZoom?.(photos, i)}
                  testId={`detail-photo-${i}`}
                  className="h-48 w-36 shrink-0 rounded-xl border border-[#342C55] object-cover"
                />
              )
            )}
          </div>
        ) : (
          <div className="mb-5 flex h-28 items-center justify-center rounded-xl border border-dashed border-[#342C55] font-manrope text-xs text-[#8B87A8]">
            {t.reserved.noPhotos}
          </div>
        )}

        <div className="space-y-3">
          <div className="flex items-center justify-between rounded-xl border border-[#342C55] px-4 py-3">
            <span className="font-manrope text-xs uppercase tracking-wide text-[#8B87A8]">{t.reserved.discountLabel}</span>
            <span className="font-manrope text-sm font-bold text-[#34D399]">
              {c.discount_percentage > 0 ? `-${c.discount_percentage}%` : "—"}
            </span>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-[#342C55] px-4 py-3">
            <span className="font-manrope text-xs uppercase tracking-wide text-[#8B87A8]">{t.reserved.statusLabel}</span>
            <span
              className={`rounded-full px-2.5 py-0.5 font-manrope text-[10px] font-bold uppercase tracking-wide ${
                c.send_status === "sent" ? "bg-[#34D399]/15 text-[#34D399]" : "bg-amber-400/15 text-amber-300"
              }`}
            >
              {c.send_status === "sent" ? t.reserved.sent : t.reserved.draft}
            </span>
          </div>
          <div className="rounded-xl border border-[#342C55] px-4 py-3">
            <span className="font-manrope text-xs uppercase tracking-wide text-[#8B87A8]">{t.reserved.descriptionLabel}</span>
            <p className="mt-1 font-manrope text-sm leading-relaxed text-[#F5F5F5]">
              {c.description && c.description !== "Nessuna descrizione" ? c.description : "—"}
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const CardItem = ({ c, i, isAdmin, onDelete, onOpen, onZoom }) => {
  const { lang, t } = useLanguage();
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: Math.min(i, 8) * 0.05, ease: EASE }}
      data-testid={`storico-card-${i}`}
      onClick={() => onOpen(c)}
      className="flex cursor-pointer gap-4 rounded-2xl border border-[#342C55] bg-[#221D3F] p-4 transition-colors duration-300 hover:border-[#34D399]/60"
    >
      <ThumbImage
        id={c.id}
        onZoom={onZoom}
        className="h-24 w-20 shrink-0 rounded-xl border border-[#342C55] object-cover"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="truncate font-manrope text-sm font-bold text-[#F5F5F5]">{c.supplier_name || "—"}</p>
          {c.discount_percentage > 0 && (
            <span className="shrink-0 rounded-full bg-[#34D399]/15 px-2 py-0.5 font-manrope text-xs font-bold text-[#34D399]">
              -{c.discount_percentage}%
            </span>
          )}
        </div>
        <p className="mt-0.5 line-clamp-2 font-manrope text-xs leading-relaxed text-[#8B87A8]">
          {c.description && c.description !== "Nessuna descrizione" ? c.description : "—"}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="font-manrope text-[11px] uppercase tracking-wide text-[#8B87A8]">
            {fmtDate(c.date || c.created_at, lang)}
          </span>
          <span
            className={`rounded-full px-2 py-0.5 font-manrope text-[10px] font-bold uppercase tracking-wide ${
              c.send_status === "sent" ? "bg-[#34D399]/15 text-[#34D399]" : "bg-amber-400/15 text-amber-300"
            }`}
          >
            {c.send_status === "sent" ? t.reserved.sent : t.reserved.draft}
          </span>
          {c.created_by && (
            <span className="font-manrope text-[11px] text-[#8B87A8]">
              {t.reserved.by} {c.created_by}
            </span>
          )}
        </div>
      </div>
      {isAdmin && (
        <button
          data-testid={`storico-delete-${i}`}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(c.id);
          }}
          aria-label={t.reserved.delete}
          className="self-start rounded-lg p-1.5 text-[#8B87A8] transition-colors duration-300 hover:bg-[#F87171]/10 hover:text-[#F87171]"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </motion.div>
  );
};

// Cache lista condivisa (memoria + sessionStorage): tornando sulla scheda o
// ricaricando la pagina i dati sono istantanei e si aggiornano in background.
const SS_KEY = "metal-class-storico-v1";
let listCache = null;
try {
  const raw = sessionStorage.getItem(SS_KEY);
  if (raw) listCache = JSON.parse(raw);
} catch {
  // cache non disponibile
}

export const fetchAllClassifications = async () => {
  const PAGE = 1000;
  let all = [];
  let skip = 0;
  for (;;) {
    const page = await getClassifications(skip, PAGE);
    const arr = Array.isArray(page) ? page : page.items ?? [];
    all = all.concat(arr);
    if (arr.length < PAGE) break;
    skip += PAGE;
  }
  all.sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at));
  listCache = all;
  try {
    sessionStorage.setItem(SS_KEY, JSON.stringify(all));
  } catch {
    // quota piena: resta la cache in memoria
  }
  return all;
};

export const prefetchStorico = () => {
  fetchAllClassifications().catch(() => {});
};

export const clearStoricoCache = () => {
  listCache = null;
  try {
    sessionStorage.removeItem(SS_KEY);
  } catch {
    // ignora
  }
};

const saveCache = (list) => {
  listCache = list;
  try {
    sessionStorage.setItem(SS_KEY, JSON.stringify(list));
  } catch {
    // ignora
  }
};

export const StoricoTab = ({ isAdmin }) => {
  const { lang, t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");
  const [view, setView] = useState("lista");
  const [selectedDay, setSelectedDay] = useState(null);
  const [detail, setDetail] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showExport, setShowExport] = useState(false);
  const [shown, setShown] = useState(60);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    setError(false);
    try {
      const all = await fetchAllClassifications();
      // Aggiorna solo se i dati sono davvero cambiati (evita re-render inutili)
      setItems((prev) =>
        prev.length === all.length &&
        prev[0]?.id === all[0]?.id &&
        prev[prev.length - 1]?.id === all[all.length - 1]?.id
          ? prev
          : all
      );
    } catch {
      if (!silent) setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (listCache) {
      // Dati già in memoria: mostra subito e aggiorna in background
      setItems(listCache);
      setLoading(false);
      load(true);
    } else {
      load();
    }
  }, []);

  const onDelete = async (id) => {
    if (!window.confirm(t.reserved.confirmDelete)) return;
    try {
      await deleteClassification(id);
      setItems((prev) => {
        const next = prev.filter((c) => c.id !== id);
        saveCache(next);
        return next;
      });
    } catch {
      // silenzioso
    }
  };

  const openDetail = async (c) => {
    if (!c.id) {
      setDetail(c);
      return;
    }
    setDetail({ ...c, photos: c.photos || [], _loading: true });
    try {
      const full = await getClassification(c.id);
      setDetail((cur) => (cur && cur.id === c.id ? { ...full } : cur));
    } catch {
      setDetail((cur) => (cur && cur.id === c.id ? { ...c } : cur));
    }
  };

  const filtered = items.filter((c) => {
    const hay = `${c.supplier_name || ""} ${c.description || ""}`.toLowerCase();
    if (!hay.includes(q.toLowerCase())) return false;
    if (selectedDay && dayKey(c.date || c.created_at) !== selectedDay) return false;
    if (!inRange(c, dateFrom, dateTo)) return false;
    return true;
  });

  useEffect(() => setShown(60), [q, dateFrom, dateTo, selectedDay]);
  const visible = filtered.slice(0, shown);

  return (
    <div data-testid="storico-tab">
      <div className="mb-5 flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8B87A8]" />
          <input
            data-testid="storico-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t.reserved.searchPh}
            className="h-12 w-full rounded-2xl border border-[#342C55] bg-[#221D3F] pl-11 pr-4 font-manrope text-sm text-[#F5F5F5] placeholder:text-[#8B87A8] focus:border-[#34D399] focus:outline-none"
          />
        </div>
        <div className="flex rounded-2xl border border-[#342C55] bg-[#221D3F] p-1">
          <button
            data-testid="storico-export"
            onClick={() => setShowExport(true)}
            disabled={items.length === 0}
            aria-label={t.reserved.exportCsv}
            title={t.reserved.exportCsv}
            className="flex items-center rounded-xl px-3.5 text-[#8B87A8] transition-colors duration-300 hover:text-[#34D399] disabled:opacity-40"
          >
            <Download className="h-4 w-4" />
          </button>
          <button
            data-testid="storico-view-lista"
            onClick={() => setView("lista")}
            aria-label={t.reserved.viewList}
            className={`flex items-center rounded-xl px-3.5 transition-colors duration-300 ${
              view === "lista" ? "bg-[#34D399] text-[#17122E]" : "text-[#8B87A8] hover:text-[#F5F5F5]"
            }`}
          >
            <List className="h-4 w-4" />
          </button>
          <button
            data-testid="storico-view-calendario"
            onClick={() => setView("calendario")}
            aria-label={t.reserved.viewCalendar}
            className={`flex items-center rounded-xl px-3.5 transition-colors duration-300 ${
              view === "calendario" ? "bg-[#34D399] text-[#17122E]" : "text-[#8B87A8] hover:text-[#F5F5F5]"
            }`}
          >
            <CalendarDays className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mb-4 flex items-start gap-3">
        <DateField value={dateFrom} onChange={setDateFrom} testId="storico-date-from" label={t.reserved.fromDate} />
        <DateField value={dateTo} onChange={setDateTo} testId="storico-date-to" label={t.reserved.toDate} />
        {(dateFrom || dateTo) && (
          <button
            data-testid="storico-dates-clear"
            onClick={() => { setDateFrom(""); setDateTo(""); }}
            aria-label={t.reserved.clearDates}
            title={t.reserved.clearDates}
            className="rounded-xl border border-[#342C55] p-2.5 text-[#8B87A8] transition-colors duration-300 hover:border-[#34D399] hover:text-[#34D399]"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {!loading && !error && items.length > 0 && (
        <p data-testid="storico-counter" className="mb-4 flex items-center gap-2 font-manrope text-xs text-[#8B87A8]">
          <ClipboardList className="h-4 w-4 text-[#34D399]" />
          <span className="font-bold text-[#F5F5F5]">{filtered.length}</span>&nbsp;/&nbsp;{items.length}{" "}
          {t.reserved.classifications.toLowerCase()}
        </p>
      )}

      {loading && (
        <div data-testid="storico-loading" className="flex justify-center py-16">
          <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-[#34D399] border-t-transparent" />
        </div>
      )}
      {error && (
        <p className="rounded-2xl border border-[#34D399]/40 bg-[#34D399]/10 px-4 py-3 font-manrope text-sm text-[#34D399]">
          {t.reserved.dataError}
        </p>
      )}

      {!loading && !error && view === "calendario" && (
        <div className="mb-6">
          <Calendar items={items} selectedDay={selectedDay} onSelect={setSelectedDay} />
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <p className="py-10 text-center font-manrope text-sm text-[#8B87A8]">{t.reserved.empty}</p>
      )}

      {!loading && !error && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {visible.map((c, i) => (
            <CardItem key={c.id ?? i} c={c} i={i} isAdmin={isAdmin} onDelete={onDelete} onOpen={openDetail} onZoom={(src) => setZoom({ photos: [src], start: 0 })} />
          ))}
        </div>
      )}

      {!loading && !error && filtered.length > shown && (
        <button
          data-testid="storico-more"
          onClick={() => setShown((s) => s + 60)}
          className="mt-6 w-full rounded-2xl border border-[#342C55] py-3.5 font-manrope text-sm font-semibold text-[#8B87A8] transition-colors duration-300 hover:border-[#34D399] hover:text-[#34D399]"
        >
          {t.reserved.showMore} ({filtered.length - shown})
        </button>
      )}

      <AnimatePresence>        {selectedDay && view === "calendario" && (
          <DayModal
            day={selectedDay}
            items={items.filter((c) => dayKey(c.date || c.created_at) === selectedDay)}
            onClose={() => setSelectedDay(null)}
            onOpen={(c) => {
              setSelectedDay(null);
              openDetail(c);
            }}
          />
        )}
        {detail && <DetailModal c={detail} onClose={() => setDetail(null)} onZoom={(photos, i) => setZoom({ photos, start: i })} />}
        {zoom && <Lightbox photos={zoom.photos} start={zoom.start} onClose={() => setZoom(null)} />}
        {showExport && <ExportModal items={items} onClose={() => setShowExport(false)} />}
      </AnimatePresence>
    </div>
  );
};
